/**
 * Provider Directory Search Component
 * Allows users to search for community-based providers by location
 */

import { useState, useEffect } from 'react';
import { Search, MapPin, Sliders, Navigation } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { providerDirectoryAPI } from '@/services/providerDirectoryApi';

interface SearchFilters {
  organizationType?: string;
  services?: string[];
  acceptsMedicaid?: boolean;
  acceptsMedicare?: boolean;
  languagesSpoken?: string[];
  minQualityScore?: number;
}

interface ProviderSearchProps {
  onSearch: (location: string, radiusMiles: number, filters: SearchFilters) => void;
  isSearching?: boolean;
}

export function ProviderSearch({ onSearch, isSearching = false }: ProviderSearchProps) {
  const [location, setLocation] = useState('');
  const [radiusMiles, setRadiusMiles] = useState(10);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState<SearchFilters>({});
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [organizationTypes, setOrganizationTypes] = useState<Array<{type: string, count: number}>>([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);

  // Fetch organization types on component mount
  useEffect(() => {
    const fetchOrganizationTypes = async () => {
      setIsLoadingTypes(true);
      try {
        const types = await providerDirectoryAPI.getOrganizationTypes();
        setOrganizationTypes(types);
      } catch (error) {
        console.error('Failed to load organization types:', error);
        // Fallback to hardcoded types if API fails
        setOrganizationTypes([
          { type: 'FQHC', count: 0 },
          { type: 'Healthcare Provider', count: 0 },
          { type: 'Mental Health', count: 0 },
          { type: 'Social Services', count: 0 },
          { type: 'Substance Abuse Treatment', count: 0 }
        ]);
      } finally {
        setIsLoadingTypes(false);
      }
    };

    fetchOrganizationTypes();
  }, []);

  const commonServices = [
    'Primary Care',
    'Dental Services',
    'Mental Health Services',
    'Substance Abuse Treatment',
    'Behavioral Health',
    'Pharmacy Services',
    'Food Assistance',
    'Housing Assistance',
    'Transportation',
    'Case Management'
  ];

  const languages = [
    'English',
    'Spanish',
    'Mandarin',
    'Vietnamese',
    'Korean',
    'Russian',
    'Arabic',
    'Tagalog',
    'French',
    'Haitian Creole'
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (location.trim()) {
      onSearch(location, radiusMiles, filters);
    }
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setIsGettingLocation(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        // Set location as coordinates - the API will handle these
        setLocation(`${latitude.toFixed(6)},${longitude.toFixed(6)}`);
        setIsGettingLocation(false);

        // Auto-trigger search with current location
        onSearch(`${latitude.toFixed(6)},${longitude.toFixed(6)}`, radiusMiles, filters);
      },
      (error) => {
        console.error('Geolocation error:', error);
        alert('Could not get your location. Please check your browser permissions and try again.');
        setIsGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const toggleService = (service: string) => {
    const currentServices = filters.services || [];
    const newServices = currentServices.includes(service)
      ? currentServices.filter(s => s !== service)
      : [...currentServices, service];
    handleFilterChange('services', newServices);
  };

  const toggleLanguage = (language: string) => {
    const currentLanguages = filters.languagesSpoken || [];
    const newLanguages = currentLanguages.includes(language)
      ? currentLanguages.filter(l => l !== language)
      : [...currentLanguages, language];
    handleFilterChange('languagesSpoken', newLanguages);
  };

  return (
    <div>
      <form onSubmit={handleSearch} className="space-y-4">
        {/* Location Search */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Search Location
          </label>
          <div className="flex space-x-2">
            <div className="flex-1 relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 flex-shrink-0" />
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="ZIP code, city, state, or address (e.g., 94103, Houston TX, New York NY)"
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                required
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={handleUseMyLocation}
              disabled={isGettingLocation || isSearching}
              icon={<Navigation className={`w-5 h-5 flex-shrink-0 ${isGettingLocation ? 'animate-pulse' : ''}`} />}
              title="Use my current location"
              aria-label="Use my current location"
            />
            <Button
              type="submit"
              variant="primary"
              disabled={isSearching}
              loading={isSearching}
              icon={<Search className="w-5 h-5 flex-shrink-0" />}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </Button>
          </div>
          <p className="mt-1.5 text-xs text-gray-500">
            Search by ZIP code, city + state, or full address across the entire USA. Or click <Navigation className="w-3 h-3 inline" /> to use your current location.
          </p>
        </div>

        {/* Radius Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Search Radius: {radiusMiles} miles
          </label>
          <input
            type="range"
            min="1"
            max="50"
            step="1"
            value={radiusMiles}
            onChange={(e) => setRadiusMiles(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>1 mi</span>
            <span>25 mi</span>
            <span>50 mi</span>
          </div>
        </div>

        {/* Advanced Filters Toggle */}
        <Button
          type="button"
          variant="ghost"
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          icon={<Sliders className="w-4 h-4 flex-shrink-0" />}
          className="text-blue-600 hover:text-blue-700 font-medium justify-start px-0"
        >
          {showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters
        </Button>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="space-y-6 pt-4 border-t border-gray-200 dark:border-gray-600">
            {/* Organization Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Organization Type
              </label>
              <select
                value={filters.organizationType || ''}
                onChange={(e) => handleFilterChange('organizationType', e.target.value || undefined)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                disabled={isLoadingTypes}
              >
                <option value="">{isLoadingTypes ? 'Loading types...' : 'All Types'}</option>
                {organizationTypes.map(typeObj => (
                  <option key={typeObj.type} value={typeObj.type}>
                    {typeObj.type} ({typeObj.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Services */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Services Needed
              </label>
              <div className="grid grid-cols-2 gap-2">
                {commonServices.map(service => (
                  <label key={service} className="flex items-center space-x-2 text-sm">
                    <input
                      type="checkbox"
                      checked={filters.services?.includes(service) || false}
                      onChange={() => toggleService(service)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span>{service}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Insurance */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Insurance Accepted
              </label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2 text-sm">
                  <input
                    type="checkbox"
                    checked={filters.acceptsMedicaid || false}
                    onChange={(e) => handleFilterChange('acceptsMedicaid', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>Accepts Medicaid</span>
                </label>
                <label className="flex items-center space-x-2 text-sm">
                  <input
                    type="checkbox"
                    checked={filters.acceptsMedicare || false}
                    onChange={(e) => handleFilterChange('acceptsMedicare', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>Accepts Medicare</span>
                </label>
              </div>
            </div>

            {/* Languages */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Languages Spoken
              </label>
              <div className="grid grid-cols-2 gap-2">
                {languages.map(language => (
                  <label key={language} className="flex items-center space-x-2 text-sm">
                    <input
                      type="checkbox"
                      checked={filters.languagesSpoken?.includes(language) || false}
                      onChange={() => toggleLanguage(language)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span>{language}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Quality Score */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Minimum Quality Score: {filters.minQualityScore || 0}
              </label>
              <input
                type="range"
                min="0"
                max="100"
                step="10"
                value={filters.minQualityScore || 0}
                onChange={(e) => handleFilterChange('minQualityScore', Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0</span>
                <span>50</span>
                <span>100</span>
              </div>
            </div>

            {/* Clear Filters */}
            <Button
              type="button"
              variant="ghost"
              onClick={() => setFilters({})}
              className="text-sm text-gray-600 hover:text-gray-800 underline px-0 justify-start"
            >
              Clear all filters
            </Button>
          </div>
        )}
        </form>
    </div>
  );
}
